import { useState, useEffect } from 'react'
import { 
  QrCode, 
  CheckCircle, 
  XCircle, 
  Play, 
  Pause, 
  Trash, 
  Plus,
  ChatCircle,
  Gear,
  ArrowClockwise,
  Warning,
  Info,
  PaperPlaneTilt,
  PencilSimple
} from 'phosphor-react'
import { useMessaging } from '../hooks/useMessaging'
import { WhatsAppInstance } from '../types'
import { getConfig } from '../config/constants'

const config = getConfig()
const API_BASE_URL = config.API_URL

export default function Messaging() {
  const {
    instances,
    templates,
    settings,
    loading,
    error,
    fetchInstances,
    createInstance,
    getInstanceQR,
    getInstanceStatus,
    deleteInstance,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    fetchSettings,
    updateSettings,
    sendTestMessage,
    extractVariables,
    getStatusText,
    getStatusColor,
    getTypeLabel,
    clearError
  } = useMessaging()

  const [activeTab, setActiveTab] = useState<'instances' | 'templates' | 'settings'>('instances')
  const [showCreateInstance, setShowCreateInstance] = useState(false)
  const [showCreateTemplate, setShowCreateTemplate] = useState(false)
  const [showTestMessage, setShowTestMessage] = useState(false)
  const [newInstanceName, setNewInstanceName] = useState('')
  const [newInstancePhone, setNewInstancePhone] = useState('')
  const [newInstanceOwnerType, setNewInstanceOwnerType] = useState('official')
  const [newInstanceOwnerId, setNewInstanceOwnerId] = useState('')
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    type: 'welcome' as any,
    content: '',
    isActive: true
  })
  const [testMessage, setTestMessage] = useState({
    instanceId: '',
    templateId: '',
    phone: '',
    variables: {} as Record<string, string>
  })
  const [updatingSettings, setUpdatingSettings] = useState(false)
  const [testingApi, setTestingApi] = useState(false)
  const [apiTestResult, setApiTestResult] = useState<{
    success: boolean
    message: string
    details?: any
  } | null>(null)
  const [fetchingApiKey, setFetchingApiKey] = useState(false)
  const [apiKeyResult, setApiKeyResult] = useState<{
    success: boolean
    message: string
    apiKey?: string
    instructions?: string[]
  } | null>(null)
  
  // Estados para QR Code
  const [showQRCode, setShowQRCode] = useState<string | null>(null)
  const [qrCodeData, setQrCodeData] = useState<string>('')
  const [loadingQR, setLoadingQR] = useState(false)
  const [checkingStatus, setCheckingStatus] = useState(false)
  
  // Estados para edição de instância
  const [showEditInstance, setShowEditInstance] = useState(false)
  const [editingInstance, setEditingInstance] = useState<WhatsAppInstance | null>(null)
  const [editInstanceData, setEditInstanceData] = useState({
    name: '',
    webhookUrl: '',
    webhookEnabled: false
  })
  const [newSettings, setNewSettings] = useState({
    evolutionApiUrl: '',
    evolutionApiToken: '',
    autoWelcomeEnabled: false,
    dailyRemindersEnabled: false,
    motivationalMessagesEnabled: false,
    reminderTime: '09:00',
    motivationalDay: 'monday' as any,
    maxMessagesPerDay: 100,
    cooldownMinutes: 60
  })

  // Carregar configurações quando a aba for alterada
  useEffect(() => {
    if (activeTab === 'settings' && !settings) {
      fetchSettings()
    }
  }, [activeTab, settings, fetchSettings])

  // Atualizar configurações quando settings mudar
  useEffect(() => {
    if (settings) {
      setNewSettings({
        evolutionApiUrl: settings.evolutionApiUrl,
        evolutionApiToken: settings.evolutionApiToken,
        autoWelcomeEnabled: settings.autoWelcomeEnabled,
        dailyRemindersEnabled: settings.dailyRemindersEnabled,
        motivationalMessagesEnabled: settings.motivationalMessagesEnabled,
        reminderTime: settings.reminderTime,
        motivationalDay: settings.motivationalDay,
        maxMessagesPerDay: settings.maxMessagesPerDay,
        cooldownMinutes: settings.cooldownMinutes
      })
    } else {
      // Configurações padrão
      setNewSettings({
        evolutionApiUrl: 'https://crme.orghi.com.br',
        evolutionApiToken: '',
        autoWelcomeEnabled: false,
        dailyRemindersEnabled: false,
        motivationalMessagesEnabled: false,
        reminderTime: '09:00',
        motivationalDay: 'monday',
        maxMessagesPerDay: 100,
        cooldownMinutes: 60
      })
    }
  }, [settings])

  const handleCreateInstance = async () => {
    if (!newInstanceName.trim() || !newInstancePhone.trim()) return
    
    // Montar número completo com +55
    const fullPhone = `+55${newInstancePhone.replace(/\D/g, '')}`

    try {
      const newInstance = await createInstance({
        name: newInstanceName,
        phone: fullPhone,
        webhookEnabled: false,
        ownerType: newInstanceOwnerType,
        ownerId: newInstanceOwnerType === 'professional' ? newInstanceOwnerId : undefined
      })
      
      setNewInstanceName('')
      setNewInstancePhone('')
      setNewInstanceOwnerType('official')
      setNewInstanceOwnerId('')
      setShowCreateInstance(false)
      
      // Atualizar status da instância recém-criada
      if (newInstance) {
        await getInstanceStatus(newInstance.id)
        
        // Se a instância está em status connecting, mostrar QR Code automaticamente
        if (newInstance.status === 'connecting') {
          handleShowQRCode(newInstance.id)
        }
      }
    } catch (error) {
      console.error('Erro ao criar instância:', error)
    }
  }

  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim() || !newTemplate.content.trim()) return
    
    try {
      await createTemplate(newTemplate)
      setNewTemplate({ name: '', type: 'welcome', content: '', isActive: true })
      setShowCreateTemplate(false)
    } catch (error) {
      console.error('Erro ao criar template:', error)
    }
  }

  const handleSendTestMessage = async () => {
    if (!testMessage.instanceId || !testMessage.templateId || !testMessage.phone) return
    
    try {
      await sendTestMessage(testMessage)
      setTestMessage({ instanceId: '', templateId: '', phone: '', variables: {} })
      setShowTestMessage(false)
      alert('Mensagem enviada com sucesso!')
    } catch (error) {
      console.error('Erro ao enviar mensagem de teste:', error)
    }
  }

  const handleUpdateSettings = async () => {
    setUpdatingSettings(true)
    try {
      await updateSettings(newSettings)
      alert('Configurações atualizadas com sucesso!')
    } catch (error) {
      console.error('Erro ao atualizar configurações:', error)
    } finally {
      setUpdatingSettings(false)
    }
  }

  const handleTestApi = async () => {
    if (!newSettings.evolutionApiUrl.trim()) {
      setApiTestResult({
        success: false,
        message: 'URL da API não configurada'
      })
      return
    }

    setTestingApi(true)
    setApiTestResult(null)
    
    try {
      const response = await fetch(`${API_BASE_URL}/messaging/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({
          url: newSettings.evolutionApiUrl,
          token: newSettings.evolutionApiToken
        })
      })

      const result = await response.json()
      
      if (response.ok) {
        setApiTestResult({
          success: true,
          message: 'Conexão com Evolution API estabelecida com sucesso!',
          details: result.data
        })
      } else {
        setApiTestResult({
          success: false,
          message: result.error || 'Erro ao conectar com Evolution API',
          details: result
        })
      }
    } catch (error) {
      setApiTestResult({
        success: false,
        message: 'Erro de rede ao testar conexão',
        details: error
      })
    } finally {
      setTestingApi(false)
    }
  }

  const handleFetchApiKey = async () => {
    if (!newSettings.evolutionApiUrl.trim()) {
      setApiKeyResult({
        success: false,
        message: 'URL da API não configurada'
      })
      return
    }

    setFetchingApiKey(true)
    setApiKeyResult(null)
    
    try {
      const response = await fetch(`${API_BASE_URL}/messaging/get-api-key?url=${encodeURIComponent(newSettings.evolutionApiUrl)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      })

      const result = await response.json()
      
      if (response.ok) {
        setApiKeyResult({
          success: true,
          message: result.data.message,
          apiKey: result.data.apiKey,
          instructions: result.data.instructions
        })
        
        // Se encontrou a API key, preencher automaticamente
        if (result.data.apiKey && result.data.apiKey !== 'Não encontrada automaticamente') {
          setNewSettings(prev => ({ ...prev, evolutionApiToken: result.data.apiKey }))
        }
      } else {
        setApiKeyResult({
          success: false,
          message: result.error || 'Erro ao buscar API key',
          instructions: result.instructions
        })
      }
    } catch (error) {
      setApiKeyResult({
        success: false,
        message: 'Erro de rede ao buscar API key',
        instructions: [
          '1. Acesse manualmente: https://crme.orghi.com.br/manager',
          '2. Procure por "API Key" ou "Token"',
          '3. Copie a chave e cole no campo acima'
        ]
      })
    } finally {
      setFetchingApiKey(false)
    }
  }

  const handleRefreshInstance = async (instanceId: string) => {
    try {
      await getInstanceStatus(instanceId)
    } catch (error) {
      console.error('Erro ao atualizar status:', error)
    }
  }

  const handleShowQRCode = async (instanceId: string) => {
    console.log('🔍 Iniciando busca do QR Code para instância:', instanceId)
    setLoadingQR(true)
    setShowQRCode(instanceId)
    
    try {
      const qrData = await getInstanceQR(instanceId)
      console.log('🔍 QR Data recebido:', qrData)
      
      if (qrData?.qrCode) {
        console.log('🔍 QR Code encontrado, tamanho:', qrData.qrCode.length)
        console.log('🔍 Primeiros 50 caracteres:', qrData.qrCode.substring(0, 50))
        setQrCodeData(qrData.qrCode)
        
        // Iniciar verificação automática de status
        startStatusPolling(instanceId)
      } else {
        console.log('❌ QR Code não encontrado no response')
        console.log('❌ Dados recebidos:', qrData)
        setQrCodeData('')
      }
    } catch (error) {
      console.error('❌ Erro ao buscar QR Code:', error)
      setQrCodeData('')
    } finally {
      setLoadingQR(false)
    }
  }

  // Função para verificar status periodicamente
  const startStatusPolling = (instanceId: string) => {
    console.log('🔄 Iniciando verificação automática de status para instância:', instanceId)
    setCheckingStatus(true)
    
    const checkStatus = async () => {
      try {
        const statusData = await getInstanceStatus(instanceId)
        console.log('🔄 Status atual:', statusData?.status)
        
        if (statusData?.status === 'connected') {
          console.log('✅ WhatsApp conectado! Parando verificação automática.')
          setCheckingStatus(false)
          setQrCodeData('') // Limpar QR Code
          setShowQRCode(null) // Fechar modal
          await fetchInstances() // Atualizar lista de instâncias
          return true // Parar polling
        } else if (statusData?.status === 'error') {
          console.log('❌ Erro na conexão. Parando verificação automática.')
          setCheckingStatus(false)
          return true // Parar polling
        }
        
        return false // Continuar polling
      } catch (error) {
        console.error('❌ Erro ao verificar status:', error)
        return false // Continuar polling mesmo com erro
      }
    }
    
    // Verificar a cada 3 segundos
    const interval = setInterval(async () => {
      const shouldStop = await checkStatus()
      if (shouldStop) {
        clearInterval(interval)
      }
    }, 3000)
    
    // Parar após 2 minutos (40 verificações)
    setTimeout(() => {
      clearInterval(interval)
      setCheckingStatus(false)
      console.log('⏰ Tempo limite atingido. Parando verificação automática.')
    }, 120000)
  }

  const handleCloseQRCode = () => {
    setShowQRCode(null)
    setQrCodeData('')
    setCheckingStatus(false)
  }

  const handleEditInstance = (instance: WhatsAppInstance) => {
    setEditingInstance(instance)
    setEditInstanceData({
      name: instance.name,
      webhookUrl: instance.webhookUrl || '',
      webhookEnabled: instance.webhookEnabled
    })
    setShowEditInstance(true)
  }

  const handleSaveInstance = async () => {
    if (!editingInstance || !editInstanceData.name.trim()) return
    
    try {
      // Aqui você implementaria a função de atualizar instância no hook
      // await updateInstance(editingInstance.id, editInstanceData)
      
      setShowEditInstance(false)
      setEditingInstance(null)
      setEditInstanceData({ name: '', webhookUrl: '', webhookEnabled: false })
      
      // Recarregar instâncias
      await fetchInstances()
    } catch (error) {
      console.error('Erro ao salvar instância:', error)
    }
  }

  const handleGenerateQRCode = async () => {
    if (!editingInstance) return
    
    setLoadingQR(true)
    setShowQRCode(editingInstance.id)
    
    try {
      const qrData = await getInstanceQR(editingInstance.id)
      console.log('🔍 QR Data recebido (edição):', qrData)
      
      if (qrData?.qrCode) {
        console.log('🔍 QR Code encontrado (edição), tamanho:', qrData.qrCode.length)
        setQrCodeData(qrData.qrCode)
        
        // Iniciar verificação automática de status
        startStatusPolling(editingInstance.id)
      } else {
        console.log('❌ QR Code não encontrado no response (edição)')
        setQrCodeData('')
      }
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error)
      setQrCodeData('')
    } finally {
      setLoadingQR(false)
    }
  }

  const handleToggleTemplate = async (templateId: string, isActive: boolean) => {
    try {
      await updateTemplate(templateId, { isActive: !isActive })
    } catch (error) {
      console.error('Erro ao atualizar template:', error)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mensageria WhatsApp</h1>
          <p className="text-gray-600 mt-1">
            Configure instâncias do WhatsApp e templates de mensagens para interagir com usuários
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowTestMessage(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
          >
            <PaperPlaneTilt size={20} className="mr-2" />
            Enviar Teste
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Gear size={20} className="mr-2" />
            Configurações
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center">
            <XCircle size={20} className="text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
            <button
              onClick={clearError}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('instances')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'instances'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Instâncias ({instances.length})
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Templates ({templates.length})
          </button>
        </nav>
      </div>

      {/* Instâncias */}
      {activeTab === 'instances' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Instâncias WhatsApp</h2>
            <button
              onClick={() => setShowCreateInstance(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              <Plus size={20} className="mr-2" />
              Nova Instância
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="spinner border-4 border-gray-200 border-t-blue-600 rounded-full w-8 h-8 animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando instâncias...</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {instances.map((instance) => (
                <div key={instance.id} className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{instance.name}</h3>
                      <p className="text-sm text-gray-500">
                        Criada em {new Date(instance.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditInstance(instance)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                        title="Editar instância"
                      >
                        <PencilSimple size={20} />
                      </button>
                      <button
                        onClick={() => handleRefreshInstance(instance.id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                        title="Atualizar status"
                      >
                        <ArrowClockwise size={20} />
                      </button>
                      <button
                        onClick={() => deleteInstance(instance.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                        title="Deletar instância"
                      >
                        <Trash size={20} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Status:</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(instance.status)}`}>
                        {getStatusText(instance.status)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Tipo:</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        instance.ownerType === 'official' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {instance.ownerType === 'official' ? 'Oficial' : 'Profissional'}
                      </span>
                    </div>

                    {instance.phone && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Telefone:</span>
                        <span className="text-sm text-gray-900">{instance.phone}</span>
                      </div>
                    )}

                    {instance.status === 'connected' && (
                      <div className="flex items-center text-green-600">
                        <CheckCircle size={20} className="mr-2" />
                        <span className="text-sm font-medium">Conectado e pronto para uso</span>
                      </div>
                    )}

                    {instance.status === 'error' && (
                      <div className="flex items-center text-red-600">
                        <XCircle size={20} className="mr-2" />
                        <span className="text-sm font-medium">Erro na conexão</span>
                      </div>
                    )}

                    {instance.status === 'connecting' && (
                      <div className="space-y-3">
                        <div className="flex items-center text-yellow-600">
                          <QrCode size={20} className="mr-2" />
                          <span className="text-sm font-medium">Aguardando conexão</span>
                        </div>
                        <button
                          onClick={() => handleShowQRCode(instance.id)}
                          className="w-full px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                        >
                          Mostrar QR Code
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {instances.length === 0 && !loading && (
            <div className="text-center py-12">
              <ChatCircle size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma instância configurada</h3>
              <p className="text-gray-500 mb-4">
                Crie sua primeira instância do WhatsApp para começar a enviar mensagens
              </p>
              <button
                onClick={() => setShowCreateInstance(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <Plus size={20} className="mr-2" />
                Criar Primeira Instância
              </button>
            </div>
          )}
        </div>
      )}

      {/* Templates */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Templates de Mensagens</h2>
            <button
              onClick={() => setShowCreateTemplate(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              <Plus size={20} className="mr-2" />
              Novo Template
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="spinner border-4 border-gray-200 border-t-blue-600 rounded-full w-8 h-8 animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando templates...</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
                <div key={template.id} className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getTypeLabel(template.type)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleTemplate(template.id, template.isActive)}
                        className={`p-2 rounded-md ${
                          template.isActive 
                            ? 'text-red-600 hover:bg-red-50' 
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={template.isActive ? 'Desativar' : 'Ativar'}
                      >
                        {template.isActive ? <Pause size={20} /> : <Play size={20} />}
                      </button>
                      <button
                        onClick={() => deleteTemplate(template.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                        title="Deletar template"
                      >
                        <Trash size={20} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Mensagem:</span>
                      <p className="text-sm text-gray-900 mt-1 line-clamp-3">{template.content}</p>
                    </div>

                    {template.variables.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">Variáveis:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {template.variables.map((variable) => (
                            <span
                              key={variable}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {`{{${variable}}}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        Usado {template.usageCount} vezes
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {template.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {templates.length === 0 && !loading && (
            <div className="text-center py-12">
              <ChatCircle size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum template criado</h3>
              <p className="text-gray-500 mb-4">
                Crie templates de mensagens para automatizar a comunicação com usuários
              </p>
              <button
                onClick={() => setShowCreateTemplate(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <Plus size={20} className="mr-2" />
                Criar Primeiro Template
              </button>
            </div>
          )}
        </div>
      )}

      {/* Configurações */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <h2 className="text-lg font-medium text-gray-900">Configurações da Mensageria</h2>
          
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-4">Configurações da Evolution API</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL da API
                    </label>
                    <input
                      type="text"
                      value={newSettings.evolutionApiUrl}
                      onChange={(e) => setNewSettings(prev => ({ ...prev, evolutionApiUrl: e.target.value }))}
                      placeholder="https://crme.orghi.com.br"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Token de Acesso
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="password"
                        value={newSettings.evolutionApiToken}
                        onChange={(e) => setNewSettings(prev => ({ ...prev, evolutionApiToken: e.target.value }))}
                        placeholder="Seu token de acesso"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={handleFetchApiKey}
                        disabled={fetchingApiKey || !newSettings.evolutionApiUrl.trim()}
                        className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Buscar API key automaticamente"
                      >
                        {fetchingApiKey ? (
                          <div className="spinner border-2 border-blue-600 border-t-transparent rounded-full w-4 h-4 animate-spin"></div>
                        ) : (
                          <Info size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Botão de teste da API */}
                <div className="mt-4">
                  <button
                    onClick={handleTestApi}
                    disabled={testingApi || !newSettings.evolutionApiUrl.trim()}
                    className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {testingApi ? (
                      <>
                        <div className="spinner border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin mr-2"></div>
                        Testando...
                      </>
                    ) : (
                      <>
                        <ArrowClockwise size={16} className="mr-2" />
                        Testar Conexão
                      </>
                    )}
                  </button>
                </div>

                {/* Resultado do teste */}
                {apiTestResult && (
                  <div className={`mt-4 p-4 rounded-md border ${
                    apiTestResult.success 
                      ? 'bg-green-50 border-green-200 text-green-800' 
                      : 'bg-red-50 border-red-200 text-red-800'
                  }`}>
                    <div className="flex items-start">
                      {apiTestResult.success ? (
                        <CheckCircle size={20} className="mr-2 mt-0.5" />
                      ) : (
                        <XCircle size={20} className="mr-2 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium">{apiTestResult.message}</p>
                        {apiTestResult.details && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-sm opacity-75">Ver detalhes</summary>
                            <pre className="mt-2 text-xs bg-black bg-opacity-10 p-2 rounded overflow-auto">
                              {JSON.stringify(apiTestResult.details, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Resultado da busca da API key */}
                {apiKeyResult && (
                  <div className={`mt-4 p-4 rounded-md border ${
                    apiKeyResult.success 
                      ? 'bg-blue-50 border-blue-200 text-blue-800' 
                      : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                  }`}>
                    <div className="flex items-start">
                      {apiKeyResult.success ? (
                        <Info size={20} className="mr-2 mt-0.5" />
                      ) : (
                        <Warning size={20} className="mr-2 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium">{apiKeyResult.message}</p>
                        {apiKeyResult.apiKey && apiKeyResult.apiKey !== 'Não encontrada automaticamente' && (
                          <p className="text-sm mt-1">
                            <strong>API Key encontrada:</strong> {apiKeyResult.apiKey.substring(0, 10)}...
                          </p>
                        )}
                        {apiKeyResult.instructions && (
                          <div className="mt-2">
                            <p className="text-sm font-medium">Instruções:</p>
                            <ul className="text-sm mt-1 space-y-1">
                              {apiKeyResult.instructions.map((instruction, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-xs mr-2 mt-1">•</span>
                                  <span>{instruction}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-md font-medium text-gray-900 mb-4">Configurações de Mensagens</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Envio automático de boas-vindas</span>
                      <p className="text-sm text-gray-500">Enviar mensagem de boas-vindas para novos usuários</p>
                    </div>
                    <button 
                      onClick={() => setNewSettings(prev => ({ ...prev, autoWelcomeEnabled: !prev.autoWelcomeEnabled }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        newSettings.autoWelcomeEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        newSettings.autoWelcomeEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Lembretes diários</span>
                      <p className="text-sm text-gray-500">Enviar lembretes para registrar peso e atividades</p>
                    </div>
                    <button 
                      onClick={() => setNewSettings(prev => ({ ...prev, dailyRemindersEnabled: !prev.dailyRemindersEnabled }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        newSettings.dailyRemindersEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        newSettings.dailyRemindersEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Mensagens motivacionais</span>
                      <p className="text-sm text-gray-500">Enviar mensagens motivacionais semanais</p>
                    </div>
                    <button 
                      onClick={() => setNewSettings(prev => ({ ...prev, motivationalMessagesEnabled: !prev.motivationalMessagesEnabled }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        newSettings.motivationalMessagesEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        newSettings.motivationalMessagesEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Horário dos Lembretes
                      </label>
                      <input
                        type="time"
                        value={newSettings.reminderTime}
                        onChange={(e) => setNewSettings(prev => ({ ...prev, reminderTime: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dia das Mensagens Motivacionais
                      </label>
                      <select
                        value={newSettings.motivationalDay}
                        onChange={(e) => setNewSettings(prev => ({ ...prev, motivationalDay: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="monday">Segunda-feira</option>
                        <option value="tuesday">Terça-feira</option>
                        <option value="wednesday">Quarta-feira</option>
                        <option value="thursday">Quinta-feira</option>
                        <option value="friday">Sexta-feira</option>
                        <option value="saturday">Sábado</option>
                        <option value="sunday">Domingo</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Máximo de Mensagens por Dia
                      </label>
                      <input
                        type="number"
                        value={newSettings.maxMessagesPerDay}
                        onChange={(e) => setNewSettings(prev => ({ ...prev, maxMessagesPerDay: parseInt(e.target.value) }))}
                        min="1"
                        max="1000"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cooldown entre Mensagens (minutos)
                      </label>
                      <input
                        type="number"
                        value={newSettings.cooldownMinutes}
                        onChange={(e) => setNewSettings(prev => ({ ...prev, cooldownMinutes: parseInt(e.target.value) }))}
                        min="1"
                        max="1440"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button 
                  onClick={handleUpdateSettings}
                  disabled={updatingSettings}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updatingSettings ? 'Salvando...' : 'Salvar Configurações'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Criar Instância */}
      {showCreateInstance && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Nova Instância WhatsApp</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome da Instância
                </label>
                <input
                  type="text"
                  value={newInstanceName}
                  onChange={(e) => setNewInstanceName(e.target.value)}
                  placeholder="Ex: Snapness Principal"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Proprietário
                </label>
                <select
                  value={newInstanceOwnerType}
                  onChange={(e) => setNewInstanceOwnerType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="official">Oficial (Snapness)</option>
                  <option value="professional">Profissional</option>
                </select>
              </div>
              
              {newInstanceOwnerType === 'professional' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ID do Profissional
                  </label>
                  <input
                    type="text"
                    value={newInstanceOwnerId}
                    onChange={(e) => setNewInstanceOwnerId(e.target.value)}
                    placeholder="ID do profissional (opcional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Deixe vazio para associar posteriormente</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Telefone (Brasil)
                </label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-l-md text-gray-500 select-none">+55</span>
                  <input
                    type="tel"
                    value={newInstancePhone}
                    onChange={(e) => setNewInstancePhone(e.target.value.replace(/\D/g, ''))}
                    placeholder="Ex: 11999999999"
                    className="w-full px-3 py-2 border-t border-b border-r border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    pattern="^\d{10,11}$"
                    maxLength={11}
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Digite apenas DDD + telefone, exemplo: 11999999999</p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="flex items-start">
                  <Info size={20} className="text-blue-600 mr-2 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Como funciona:</p>
                    <p className="mt-1">
                      1. Digite um nome para a instância<br/>
                      2. Selecione o tipo de proprietário<br/>
                      3. Informe o número de telefone (com DDI)<br/>
                      4. A instância será criada na Evolution API<br/>
                      5. Use o botão "Atualizar status" para verificar conexão<br/>
                      6. Configure webhooks se necessário
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateInstance(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateInstance}
                disabled={loading || !newInstanceName.trim() || !newInstancePhone.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Criando...' : 'Criar Instância'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Criar Template */}
      {showCreateTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Novo Template de Mensagem</h3>
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome do Template
                  </label>
                  <input
                    type="text"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Boas-vindas"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Mensagem
                  </label>
                  <select
                    value={newTemplate.type}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="welcome">Boas-vindas</option>
                    <option value="reminder">Lembrete</option>
                    <option value="motivation">Motivação</option>
                    <option value="goal">Meta</option>
                    <option value="custom">Personalizado</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conteúdo da Mensagem
                </label>
                <textarea
                  value={newTemplate.content}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Digite sua mensagem aqui... Use {{variavel}} para variáveis dinâmicas"
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Variáveis disponíveis: name, goal, weight, activity
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex items-start">
                  <Warning size={20} className="text-yellow-600 mr-2 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Dicas para mensagens eficazes:</p>
                    <ul className="mt-1 space-y-1">
                      <li>• Seja pessoal e motivacional</li>
                      <li>• Use emojis para tornar mais amigável</li>
                      <li>• Mantenha um tom positivo e encorajador</li>
                      <li>• Inclua call-to-actions claros</li>
                      <li>• Evite ser muito invasivo ou insistente</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateTemplate(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateTemplate}
                disabled={loading || !newTemplate.name.trim() || !newTemplate.content.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Criando...' : 'Criar Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Enviar Teste */}
      {showTestMessage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Enviar Mensagem de Teste</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instância
                </label>
                <select
                  value={testMessage.instanceId}
                  onChange={(e) => setTestMessage(prev => ({ ...prev, instanceId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione uma instância</option>
                  {instances.filter(inst => inst.status === 'connected').map(instance => (
                    <option key={instance.id} value={instance.id}>{instance.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template
                </label>
                <select
                  value={testMessage.templateId}
                  onChange={(e) => setTestMessage(prev => ({ ...prev, templateId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione um template</option>
                  {templates.filter(template => template.isActive).map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Telefone
                </label>
                <input
                  type="text"
                  value={testMessage.phone}
                  onChange={(e) => setTestMessage(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+55 11 99999-9999"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowTestMessage(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendTestMessage}
                disabled={loading || !testMessage.instanceId || !testMessage.templateId || !testMessage.phone}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Enviando...' : 'Enviar Teste'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição da Instância */}
      {showEditInstance && editingInstance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Editar Instância</h3>
              <button
                onClick={() => setShowEditInstance(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome da Instância
                </label>
                <input
                  type="text"
                  value={editInstanceData.name}
                  onChange={(e) => setEditInstanceData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL do Webhook (opcional)
                </label>
                <input
                  type="url"
                  value={editInstanceData.webhookUrl}
                  onChange={(e) => setEditInstanceData(prev => ({ ...prev, webhookUrl: e.target.value }))}
                  placeholder="https://exemplo.com/webhook"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="webhookEnabled"
                  checked={editInstanceData.webhookEnabled}
                  onChange={(e) => setEditInstanceData(prev => ({ ...prev, webhookEnabled: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="webhookEnabled" className="ml-2 block text-sm text-gray-900">
                  Ativar webhook
                </label>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Status: {editingInstance.status}</span>
                  <span className="text-sm text-gray-500">{editingInstance.phone || 'Não conectado'}</span>
                </div>
                
                <button
                  onClick={handleGenerateQRCode}
                  disabled={loadingQR}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingQR ? 'Gerando...' : 'Gerar QR Code'}
                </button>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowEditInstance(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveInstance}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal QR Code */}
      {showQRCode && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">QR Code da Instância</h3>
            <div className="flex flex-col items-center justify-center p-4">
              {loadingQR ? (
                <div className="spinner border-4 border-gray-200 border-t-blue-600 rounded-full w-16 h-16 animate-spin mx-auto mb-4"></div>
              ) : qrCodeData ? (
                <>
                  <div className="mb-4 p-2 bg-gray-100 rounded text-xs text-gray-600">
                    QR Code carregado: {qrCodeData.length} caracteres
                  </div>
                  <img 
                    src={qrCodeData} 
                    alt="QR Code" 
                    className="max-w-full h-auto border border-gray-200 rounded"
                    onLoad={() => console.log('✅ Imagem do QR Code carregada com sucesso')}
                    onError={(e) => console.error('❌ Erro ao carregar imagem do QR Code:', e)}
                  />
                  
                  {/* Status da verificação automática */}
                  {checkingStatus && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="flex items-center justify-center">
                        <div className="spinner border-2 border-blue-600 border-t-transparent rounded-full w-4 h-4 animate-spin mr-2"></div>
                        <span className="text-sm text-blue-800">
                          Verificando conexão automaticamente...
                        </span>
                      </div>
                      <p className="text-xs text-blue-600 mt-1 text-center">
                        O modal fechará automaticamente quando conectado
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center text-gray-500">
                  <p>QR Code não disponível</p>
                  <p className="text-sm mt-2">Verifique se a instância está em status "connecting"</p>
                </div>
              )}
              <p className="text-sm text-gray-500 mt-4">
                Escaneie este QR Code com o aplicativo do WhatsApp para conectar sua instância.
              </p>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleCloseQRCode}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 